import { _ } from "libs";

import React, { Component, createRef } from "react";
import MessageGroup from "./messageGroup";
import { ChatConversation, MessagePartner } from "../../types";
import { Subscription, Subject } from "rxjs";
import { map, filter, bufferTime } from "rxjs/operators";
import withRedux from "../../withRedux";
import { loadPreviousMessages, messageReadByMe } from "../../actions/chat";
import InfiniteScroll from "../infiniteScroll";

function createBotPartner(userId: string): MessagePartner {
	return {
		userId,
		username: "gc_bot",
		firstName: _("chatBot.fullName"),
		lastName: "",
		photoUrl: "/images/avatar_gotcourts_bot.svg",
		active: true,
	};
}

const getPartner = (conversation: ChatConversation, senderId: string) => {
	let partner = conversation.partnersHash[senderId];
	if (!partner) {
		partner = createBotPartner(senderId);
	}

	return partner;
};

const createMessageGroups = (
	conversation: ChatConversation,
	userId: string,
	observer: IntersectionObserver
): Array<typeof MessageGroup> => {
	const messageGroups = [];
	let messageGroup = [];
	let partnersLastReadMessage = {};
	let messageReadBy = {};
	let lastPartnerId = null;
	const length = conversation.messages.length;

	const messages = conversation.messages.map(message => message); //to prevent reversing in place
	/*
		.filter(message => {
			if (message.text?.messageType === "message") {
				return true;
			}

			return false;
		});
		*/

	// Iterate trough messages to create object
	// of the last read message by partner
	messages.forEach((message, key) => {
		message.readBy.forEach(partner => {
			partnersLastReadMessage[partner] = message.messageId;
		});
	});

	// Create object of messages that have some read by partners
	for (var partnerId in partnersLastReadMessage) {
		if (partnersLastReadMessage.hasOwnProperty(partnerId)) {
			const messageId = partnersLastReadMessage[partnerId];
			if (!Array.isArray(messageReadBy[messageId])) {
				messageReadBy[messageId] = [];
			}
			messageReadBy[messageId].push(partnerId);
		}
	}

	//conversation.messages
	messages.reverse().forEach((message, key) => {
		let partner = getPartner(conversation, message.senderId);

		if (message.bot && !partner) {
			partner = createBotPartner(message.senderId);

			messageGroups.push(
				<MessageGroup
					key={message.messageId}
					partner={partner}
					partners={conversation.partnersHash}
					messages={messageGroup}
					observer={observer}
					messageReadBy={messageReadBy}
					userId={userId}
				/>
			);
			messageGroup = [];
		} else {
			const messageGroupEnded = message.senderId !== lastPartnerId && lastPartnerId !== null;

			if (messageGroupEnded) {
				messageGroups.push(
					<MessageGroup
						key={message.messageId}
						partner={getPartner(conversation, lastPartnerId)}
						partners={conversation.partnersHash}
						messages={messageGroup}
						observer={observer}
						messageReadBy={messageReadBy}
						userId={userId}
					/>
				);
				messageGroup = [];
			}
		}
		messageGroup.push(message);
		lastPartnerId = message.senderId;
	});

	messageGroups.push(
		<MessageGroup
			key={length}
			partner={getPartner(conversation, lastPartnerId)}
			partners={conversation.partnersHash}
			messages={messageGroup}
			observer={observer}
			messageReadBy={messageReadBy}
			userId={userId}
		/>
	);

	return messageGroups;
};

type Props = {
	conversation: ChatConversation;
	userId: string;
	loadPreviousMessages: typeof loadPreviousMessages;
	messageReadByMe: typeof messageReadByMe;
};

class Messages extends Component<Props> {
	chatbox = null; //: RefObject<HTMLDivElement>;
	observer = null as IntersectionObserver;

	subject$: Subject<IntersectionObserverEntry>;
	subscription: Subscription;

	constructor(props: Props) {
		super(props);

		this.chatbox = createRef();
		this.subject$ = new Subject();
	}

	componentDidMount() {
		this.subject$
			.pipe(
				filter(entry => {
					return entry.isIntersecting;
				}),
				map(entry => {
					//@ts-ignore
					return entry.target.dataset["id"];
				}),
				bufferTime(500)
			)
			.subscribe(messageIds => {
				if (messageIds.length > 0) {
					this.props.messageReadByMe(messageIds);
				}
			});
	}

	componentWillUnmount() {
		this.observer.disconnect();
	}

	createObserverOptions = (root: HTMLDivElement) => {
		return {
			root,
			rootMargin: "0px",
			threshold: 1.0,
		};
	};

	observerCallback = (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
		entries.forEach(entry => {
			this.subject$.next(entry);
		});
	};

	createObserver = (): IntersectionObserver => {
		if (this.observer === null) {
			const observer = new IntersectionObserver(
				this.observerCallback,
				this.createObserverOptions(this.chatbox.current)
			);
			this.observer = observer;
		}

		return this.observer;
	};

	loadMore = async (): Promise<any> => {
		const { conversation } = this.props;

		if (conversation.hasMore) {
			const oldestMessage = conversation.messages[conversation.messages.length - 1];
			const date = new Date(oldestMessage.createdAt);
			return this.props.loadPreviousMessages(conversation.conversationId, date.getTime());
		}
	};

	render() {
		const { conversation } = this.props;
		return (
			<InfiniteScroll
				loadMore={this.loadMore}
				hasMore={conversation.hasMore}
				loadMoreText={_("messages.clickToLoadMore")}
				isReverse
				className="conversationMessages"
			>
				{createMessageGroups(conversation, this.props.userId, this.createObserver())}
			</InfiniteScroll>
		);
	}
}

const mapStateToProps = () => {
	return {};
};

export default withRedux(
	{
		loadPreviousMessages,
		messageReadByMe,
	},
	mapStateToProps
)(Messages);
